import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import List, { Header } from '../components/PlayerList';
import Pagination from '../components/Pagination';
import styled from 'styled-components';
import { getPlayerList, addPlayer, PARAMS } from '../services/Player';
import Gamification from '../services/Gamification';

import AddPlayerDialog from '../components/AddPlayerDialog/AddPlayerDialog';
import Toast from '../components/Toast';
import SplitIOTreatment from '../../components/splitio/splitioTreatment';
import splitsDictionary from '../../services/utils/splitsDictionary';
import RTMOverviewDialog from '../components/RTMOverviewDialog';
import mixpanel from 'mixpanel-browser';

const REMOTE_THERAPY_MONITORING_EVENT = 'Therapy_RestoreSkillsUI_MyPlayers_Click';

const Container = styled.section`
  padding: 20px;

  @media screen and (min-width: 1200px) {
    padding: 0 max(6.42%, 224px);
  }
`;

const PlayerListContainer = styled.div`
  margin-top: 20px;
`;

const initialParams = {
  [PARAMS.PAGE]: 0,
  [PARAMS.DATE_FROM]: '',
  [PARAMS.DATE_TO]: '',
  [PARAMS.SORT_BY]: 'alpha',
  [PARAMS.SORT_DIRECTION]: 1,
  [PARAMS.NAME]: '',
  [PARAMS.PER_PAGE]: 10,
};

const PlayerList = () => {
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState({ total: 0, list: [] });
  const [searchParams, setSearchParams] = useState(initialParams);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [serverAddPlayerErrors, setServerAddPlayerErrors] = useState({});
  const [monitorPlayer, setMonitorPlayer] = useState({ homePlayerID: -1 });

  useEffect(() => {
    getPlayerList({ ...initialParams })
      .then((response) => {
        setPlayers({
          total: response.total_count,
          list: response.data,
        });
        setLoading(false);
      })
      .catch((error) => {
        throw new Error('Error retrieving patient list.', error);
      });
    Gamification.emitEvent({ action: 'access_my_players', new_event: {} }).catch((error) => console.error(error));
  }, []);

  const handleParamChange = (params) => {
    setSearchParams({ ...searchParams, ...params });

    getPlayerList({ ...params })
      .then((response) => {
        setPlayers({
          total: response.total_count,
          list: response.data,
        });
        setLoading(false);
      })
      .catch((error) => {
        throw new Error('Error retrieving patient list.', error);
      });
  };

  const handleAddPlayer = async (firstName, lastName, shouldAddHomePlayer, login, password) => {
    setAddingPlayer(true);
    try {
      const response = await addPlayer(firstName, lastName, shouldAddHomePlayer, login, password);
      if (response?.data?.success) {
        setShowAddPlayerDialog(false);
        setShowToast(true);
        handleParamChange({ ...searchParams });
      } else {
        const error = {};
        error[response.data.field] = response.data.message;
        setServerAddPlayerErrors(error);
      }
    } catch (error) {
      console.error(error);
    }

    setAddingPlayer(false);
  };

  return (
    <Container>
      {showToast && (
        <Toast
          type="success"
          message="Player Added!"
          callback={() => {
            setShowToast(false);
          }}
        />
      )}
      <SplitIOTreatment
        featureName={splitsDictionary.ADD_PLAYER.name}
        trafficType={splitsDictionary.ADD_PLAYER.traffic_type}
        treatmentValue="on"
      >
        {(treatment) => {
          if (treatment === 'on') {
            if (showAddPlayerDialog) {
              return (
                <AddPlayerDialog
                  isOpen={showAddPlayerDialog}
                  closeCallback={() => {
                    setShowAddPlayerDialog(false);
                  }}
                  submitCallback={handleAddPlayer}
                  saving={addingPlayer}
                  backendErrors={serverAddPlayerErrors}
                ></AddPlayerDialog>
              );
            } else {
              return null;
            }
          } else {
            return null;
          }
        }}
      </SplitIOTreatment>
      <Header
        params={searchParams}
        handleParamChange={handleParamChange}
        total={players.total}
        openAddPlayerDialog={() => {
          setShowAddPlayerDialog(true);
        }}
      />
      {loading && (
        <div className="ad-table-loader">
          <span className="loading-title">Loading</span>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {!loading && (
        <PlayerListContainer>
          <List
            players={players.list}
            openMonitorDialog={(player) => {
              mixpanel.track(REMOTE_THERAPY_MONITORING_EVENT, { Selection: 'RemoteTherapyMonitoring' });
              setMonitorPlayer(player);
            }}
          />
        </PlayerListContainer>
      )}

      {players.total > searchParams[PARAMS.PER_PAGE] && (
        <Pagination
          currentPage={searchParams[PARAMS.PAGE] + 1}
          total={players.total}
          callback={(newPage) => handleParamChange({ ...searchParams, [PARAMS.PAGE]: newPage - 1 })}
          displayedResults={searchParams[PARAMS.PER_PAGE]}
        />
      )}

      {monitorPlayer.homePlayerID !== -1 && (
        <RTMOverviewDialog
          player={monitorPlayer}
          closeCallback={() => {
            setMonitorPlayer({ homePlayerID: -1 });
          }}
        />
      )}
    </Container>
  );
};

export default PlayerList;
